import React from 'react'
import { OrderItem } from '../../types/Order'
import { CreateOrderPayload } from '../../types/CreateOrderPayload'
import { format } from 'date-fns'

// Constants
export const DELIVERY_TYPES = {
  HOME_DELIVERY: 1000,
  PICKUP: 1100,
} as const

export const DELIVERY_NAMES = {
  HOME_DELIVERY: 'ScheduledDelivery',
  PICKUP: 'ScheduledPickup',
} as const

export const DEFAULT_CHANNEL_NAME = 'icacatering'

// Helper to merge products with same productId, variantId, and product message
export function mergeProducts(prev: OrderItem[], newItem: OrderItem): OrderItem[] {
  const match = prev.findIndex(
    p => p.productId === newItem.productId && 
         p.variantId === newItem.variantId && 
         p.message === newItem.message
  )
  
  if (match === -1) {
    return [...prev, newItem]
  }
  
  const existingProduct = prev[match]
  const addedAmount = newItem.amount ?? newItem.quantity ?? 0
  const currentAmount = existingProduct.amount ?? existingProduct.quantity ?? 0
  const newAmount = currentAmount + addedAmount
  
  const priceGross = existingProduct.priceGross ?? existingProduct.price ?? newItem.priceGross ?? newItem.price
  const newSubTotal = (priceGross || 0) * newAmount
  const newVatSubTotal = existingProduct.vatSubTotal || newItem.vatSubTotal || 0
  
  const newIds = existingProduct.ids?.length && newItem.ids?.length
    ? [{ 
      ...existingProduct.ids[0], 
      quantity: (existingProduct.ids[0].quantity || 0) + (newItem.ids[0].quantity || 0) 
    }]
    : existingProduct.ids
  
  const updated = {
    ...existingProduct,
    amount: newAmount,
    quantity: newAmount,
    subTotal: newSubTotal,
    vatSubTotal: newVatSubTotal,
    ids: newIds,
  }
  
  return [...prev.slice(0, match), updated, ...prev.slice(match + 1)]
}

// Calculate total products price
export function calculateProductsSubTotal(products: OrderItem[]): number {
  return products.reduce((sum, p) => {
    const quantity = p.amount ?? p.quantity ?? 0
    const price = p.priceGross ?? p.price ?? 0
    return sum + (quantity * price)
  }, 0)
}

// Get article count text
export function getArticleText(products: OrderItem[]): string {
  const numberOfItems = products.reduce((prev, current) => {
    return prev + (current.amount ?? current.quantity ?? 0)
  }, 0)
  
  return numberOfItems === 1 ? '1 artikel' : `${numberOfItems} artiklar`
}

// Build order payload
export function buildOrderPayload(params: {
  storeId: string
  customerName: string
  customerEmail: string
  customerPhone: string
  deliveryAddress: string
  zipCode: string
  city: string
  deliveryDate: Date
  deliveryTimeSlot: string
  selectedDeliveryMethod: {
    deliveryType: string
    id: string
    name: string
    bongName: string
    price: number
  }
  customerComment: string
  storeComment: string
  productsToAdd: OrderItem[]
}): CreateOrderPayload {
  const {
    storeId,
    customerName,
    customerEmail,
    customerPhone,
    deliveryAddress,
    zipCode,
    city,
    deliveryDate,
    deliveryTimeSlot,
    selectedDeliveryMethod,
    customerComment,
    storeComment,
    productsToAdd,
  } = params

  return {
    storeId,
    channelName: DEFAULT_CHANNEL_NAME,
    userInformation: {
      email: customerEmail,
      phoneNumber: customerPhone,
      name: customerName,
      deliveryAddress,
      zipCode,
      city,
    },
    deliveryInformation: {
      deliveryType: selectedDeliveryMethod.deliveryType === DELIVERY_NAMES.PICKUP ? DELIVERY_TYPES.PICKUP : DELIVERY_TYPES.HOME_DELIVERY,
      deliveryDate: format(deliveryDate, 'yyyy-MM-dd'),
      deliveryTimeSlot,
      deliveryProviderId: selectedDeliveryMethod.id,
      displayName: selectedDeliveryMethod.name,
      bongDisplayName: selectedDeliveryMethod.bongName,
      deliveryMessage: customerComment,
      deliveryFee: selectedDeliveryMethod.price ?? 0,
    },
    storeComment,
    orderItems: productsToAdd.map(p => ({
      productId: p.productId,
      variantId: p.variantId,
      quantity: p.amount ?? p.quantity ?? 0,
      orderItemComment: p.message ?? undefined,
    })),
  }
}

// Scroll to ref helper
export function scrollToRef(
  ref: React.RefObject<HTMLDivElement | null>,
  options?: ScrollIntoViewOptions
): void {
  const opts: ScrollIntoViewOptions = options ?? { behavior: 'smooth', block: 'center' }
  requestAnimationFrame(() => {
    ref.current?.scrollIntoView(opts)
  })
}
